<template>
  <aside>
    <WCSidebarV2>
      <!-- All sidebar components wrapped for sizing -->
      <div
        class="wc-sidebar sidebar-v2"
        :class="{ 'sidebar-v2--nested-open': isNestedSidebarVisible }"
      >
        <div class="pt-3 d-flex flex-column h-100">
          <div class="sidebar-menu pt-0 pb-0" v-if="isCustomer || isEmployee">
            <div class="menu-item p-0 border-none">
              <button
                class="sidebar-v2__row-button"
                :aria-label="$t('myAccount')"
                @click="openMyAccountMenu"
              >
                <span class="sidebar-v2__row-main">
                  <font-awesome-icon class="side-menu-icon" icon="user" />
                  <span class="sidebar-v2__collapsible-label">{{ $t('myAccount') }}</span>
                </span>
                <font-awesome-icon class="sidebar-v2__row-chevron" icon="chevron-right" />
              </button>
            </div>
          </div>

          <div class="sidebar-menu pt-0 pb-0" v-else>
            <div class="menu-item p-0 border-none">
              <span class="sidebar-v2__row-main">
                <font-awesome-icon class="side-menu-icon" icon="user" />
                <button class="sidebar-v2__row-button sidebar-v2__row-button--auto" @click="login">
                  <span class="menu__text--color">{{ $t('signIn') }}</span>
                </button>

                <span class="font-weight-bold font-size-sm" v-if="$configuration.allowCustomerAdd"
                  >|</span
                >

                <button
                  class="sidebar-v2__row-button sidebar-v2__row-button--auto"
                  v-if="$configuration.allowCustomerAdd"
                  @click="register"
                >
                  <span class="menu__text--color"> {{ $t('register') }}</span>
                </button>
              </span>
            </div>
          </div>

          <WCNestedSidebarPanel
            ref="nestedSidebarPanel"
            class="sidebar-v2__nested-panel-host"
            :max-nested-levels="2"
            :on-item-action="performNestedAction"
            @visibility-change="onNestedSidebarVisibilityChange"
          />

          <div class="sidebar-menu">
            <h3 class="title">{{ $t('primaryShopping') }}</h3>

            <div class="mt-0">
              <div
                v-if="
                  isNotAnEmployee &&
                    ((menu && menu.children) || (circularMenu && circularMenu.length)) &&
                    !isScanAndGoMode
                "
                class="menu-item p-0"
              >
                <button
                  class="sidebar-v2__row-button"
                  :aria-label="$t('byCategory')"
                  @click="openShopByCategoryMenu"
                >
                  <span class="sidebar-v2__row-main">
                    <WCSvgIcon class="side-menu-icon" name="grid-2" icon-size="lg" />
                    <span class="sidebar-v2__collapsible-label">{{ $t('byCategory') }}</span>
                  </span>
                  <font-awesome-icon class="sidebar-v2__row-chevron" icon="chevron-right" />
                </button>
              </div>
            </div>

            <template v-if="isNotAnEmployee && quickLinks.shoppingLists">
              <div class="menu-item">
                <WCSidebarMenuItemV2
                  :key="quickLinks.shoppingLists"
                  :menu="quickLinks.shoppingLists"
                  :isDisplayChevronRight="false"
                />
              </div>
            </template>

            <template v-if="isNotAnEmployee && quickLinks.buyItAgain">
              <div class="menu-item">
                <WCSidebarMenuItemV2
                  :key="quickLinks.buyItAgain"
                  :menu="quickLinks.buyItAgain"
                  :isDisplayChevronRight="false"
                />
              </div>
            </template>

            <template v-if="isNotAnEmployee && quickLinks.orderHistory">
              <div class="menu-item">
                <WCSidebarMenuItemV2
                  :key="quickLinks.orderHistory"
                  :menu="quickLinks.orderHistory"
                  :isDisplayChevronRight="false"
                />
              </div>
            </template>
          </div>

          <div class="sidebar-menu">
            <h3 class="title">{{ $t('savingsAndOffers') }}</h3>

            <template v-if="isNotAnEmployee && quickLinks.digitalCoupons">
              <div class="menu-item">
                <WCSidebarMenuItemV2
                  :key="quickLinks.digitalCoupons"
                  :menu="quickLinks.digitalCoupons"
                  :isDisplayChevronRight="false"
                />
              </div>
            </template>

            <template v-if="isNotAnEmployee && quickLinks.weeklyAd">
              <div class="menu-item">
                <WCSidebarMenuItemV2
                  :key="quickLinks.weeklyAd"
                  :menu="quickLinks.weeklyAd"
                  :isDisplayChevronRight="false"
                />
              </div>
            </template>

            <template v-if="isNotAnEmployee && quickLinks.rewards">
              <div class="menu-item">
                <WCSidebarMenuItemV2
                  :key="quickLinks.rewards"
                  :menu="quickLinks.rewards"
                  :isDisplayChevronRight="false"
                />
              </div>
            </template>

            <template v-if="isNotAnEmployee && quickLinks.customerPrograms">
              <div class="menu-item">
                <WCSidebarMenuItemV2
                  :key="quickLinks.customerPrograms"
                  :menu="quickLinks.customerPrograms"
                  :isDisplayChevronRight="false"
                />
              </div>
            </template>

            <template v-if="isNotAnEmployee && quickLinks.egiftCards">
              <div class="menu-item">
                <WCSidebarMenuItemV2
                  :key="quickLinks.egiftCards"
                  :menu="quickLinks.egiftCards"
                  :isDisplayChevronRight="false"
                />
              </div>
            </template>
          </div>

          <div class="sidebar-menu" v-if="this.isMobile">
            <h3 class="title">{{ $t('inStore') }}</h3>

            <template v-if="isNotAnEmployee && quickLinks.acceleratedCheckout">
              <div class="menu-item">
                <WCSidebarMenuItemV2
                  :key="quickLinks.acceleratedCheckout"
                  :menu="quickLinks.acceleratedCheckout"
                  :isDisplayChevronRight="false"
                  @menu-action="onQuickLinkAction"
                />
              </div>
            </template>

            <div class="menu-item p-0" v-if="isScanAndGoMode || this.$route.query.scan === 'true'">
              <button class="sidebar-v2__row-button" @click="onClickExitScanAndGoMode">
                <span class="sidebar-v2__row-main">
                  <font-awesome-icon class="side-menu-icon" icon="barcode-read" />
                  <span class="sidebar-v2__collapsible-label">
                    {{ $t('exitAcceleratedCheckOutMode') }}</span
                  >
                </span>
              </button>
            </div>
            <template v-if="isNotAnEmployee && quickLinks.scanCustomerCard">
              <div class="menu-item">
                <WCSidebarMenuItemV2
                  :key="quickLinks.scanCustomerCard"
                  :menu="quickLinks.scanCustomerCard"
                  :isDisplayChevronRight="false"
                />
              </div>
            </template>
          </div>

          <div class="sidebar-menu">
            <h3 class="title">{{ $t('support') }}</h3>

            <template v-if="isNotAnEmployee && quickLinks.contactUs">
              <div class="menu-item">
                <WCSidebarMenuItemV2
                  :key="quickLinks.contactUs"
                  :menu="quickLinks.contactUs"
                  :isDisplayChevronRight="false"
                />
              </div>
            </template>

            <template v-if="quickLinks.accessibilityStatement">
              <div class="menu-item">
                <WCSidebarMenuItemV2
                  :key="quickLinks.accessibilityStatement"
                  :menu="quickLinks.accessibilityStatement"
                  :isDisplayChevronRight="false"
                />
              </div>
            </template>
          </div>

          <div class="sidebar-menu pt-0 pb-0" v-if="$configuration.i18nEnabled">
            <div class="menu-item p-0 border-none">
              <button
                class="sidebar-v2__row-button"
                :aria-label="$t('chooseLanguage')"
                @click="openLanguageMenu"
              >
                <span class="sidebar-v2__row-main">
                  <WCSvgIcon class="side-menu-icon" :name="'language'" icon-size="lg" />
                  <span class="sidebar-v2__collapsible-label"
                    >{{ $t('chooseLanguage') }} &middot; {{ currentLanguageName }}</span
                  >
                </span>
                <font-awesome-icon class="sidebar-v2__row-chevron" icon="chevron-right" />
              </button>
            </div>
          </div>

          <div class="sidebar-menu pt-0 pb-0" v-if="showNestedUtilityMenu">
            <div class="menu-item p-0 border-none">
              <button
                class="sidebar-v2__row-button"
                :aria-label="$t('quickLinks')"
                @click="openQuickLinksMenu"
              >
                <span class="sidebar-v2__row-main">
                  <WCSvgIcon class="side-menu-icon" :name="'quick-links'" icon-size="lg" />
                  <span class="sidebar-v2__collapsible-label">{{ $t('quickLinks') }}</span>
                </span>
                <font-awesome-icon class="sidebar-v2__row-chevron" icon="chevron-right" />
              </button>
            </div>
          </div>

          <div class="sidebar-menu pt-0 pb-0" v-if="!isEmployee && !isScanAndGoMode">
            <ul class="side-menu-list">
              <WCSideMenuItemV2>
                <router-link class="sidebar-v2-item menu__text--color" to="/employee/login">
                  <span class="sidebar-v2-item__left">
                    <WCSvgIcon class="side-menu-icon" :name="'door-open'" icon-size="lg" />
                    <span>{{ $t('employeeLogin') }}</span>
                  </span>
                </router-link>
              </WCSideMenuItemV2>
            </ul>
          </div>

          <div class="sidebar-v2__footer-wrap">
            <div
              class="sidebar-menu social-link-section"
              v-if="$configuration.socialNetworksEnabled"
            >
              <h3 class="title">{{ $t('followUs') }}</h3>
              <div>
                <ul class="social-icons unstyled-list">
                  <li v-for="(socialProvider, si) in $configuration.socialProviders" :key="si">
                    <a
                      v-if="socialProvider.provider === 'facebook'"
                      :href="`https://www.facebook.com/${socialProvider.userKey}`"
                      :aria-label="$t('facebook')"
                      target="_blank"
                      rel="noopener"
                    >
                      <WCSvgIcon class="social-icon" name="facebook" />
                    </a>
                    <a
                      v-if="socialProvider.provider === 'twitter'"
                      :href="`https://x.com/${socialProvider.userKey}`"
                      :aria-label="$t('twitter')"
                      target="_blank"
                      rel="noopener"
                    >
                      <WCSvgIcon class="social-icon" name="x-twitter" />
                    </a>
                    <a
                      v-if="socialProvider.provider === 'youtube'"
                      :href="`https://youtube.com/${socialProvider.userKey}`"
                      :aria-label="$t('youTube')"
                      target="_blank"
                      rel="noopener"
                    >
                      <WCSvgIcon class="social-icon" name="youtube" />
                    </a>
                    <a
                      v-if="socialProvider.provider === 'linkedin'"
                      :href="`https://www.linkedin.com/company/${socialProvider.userKey}`"
                      :aria-label="$t('linkedIn')"
                      target="_blank"
                      rel="noopener"
                      ><WCSvgIcon class="social-icon" name="linkedin" />
                    </a>
                    <a
                      v-if="socialProvider.provider === 'instagram'"
                      :href="`https://www.instagram.com/${socialProvider.userKey}`"
                      :aria-label="$t('instagram')"
                      target="_blank"
                      rel="noopener"
                      ><WCSvgIcon class="social-icon" name="instagram" />
                    </a>
                  </li>
                </ul>
              </div>
            </div>

            <div class="sidebar-v2__copyright-wrap">
              <div class="font-size-xs text-center mb-3">
                <font-awesome-icon :icon="['far', 'copyright']" :title="$t('copyright')" />
                <span> {{ $t('allRightsReserved', { year: currentYear }) }} </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </WCSidebarV2>
  </aside>
</template>

<script>
import axios from 'axios';
import { mapGetters } from 'vuex';
import EmployeeMixin from '@/modules/employee/mixins/EmployeeMixin';
import OrderTypesMixin from '@/modules/orderTypes/mixins/OrderTypesMixin';
import ScanAndGoMixin from '@/modules/user/mixins/ScanAndGoMixin';
import ItemGroupMixin from '@/modules/filters/mixins/ItemGroupMixin';
import GiftCardMixin from '@/modules/giftcard/mixins/GiftCardMixin';
import { PlatformMixin } from '@/modules/platform';
import ContactUsMixin from '@/mixins/ContactUsMixin';
import { THIRD_PARTY_SERVICE_TYPE } from '@/constants/ThirdPartyConstants';
import PushNotificationSupportParams from '@/models/entities/push-notifications/push-notification-support-params';
import WCSidebarV2 from '@/modules/sidebar/components/WCSideBarV2/WCSideBarV2.vue';
import WCSidebarMenuItemV2 from '@/modules/sidebar/components/WCSidebarMenuItemV2/WCSidebarMenuItemV2.vue';
import { capitalizeFirstLetter } from '@/utils/formatters';
import { USER_ROLES } from '@/constants/AppConstants';
import useFederatedLogin from '@/composables/useFederatedLogin';
import pushNotificationService from '@/services/pushNotifications/pushNotification.service';
import ModalService from '@/modules/modals/services/modal.service';
import emitter from '@/eventBus';
import { SIDE_MENU_EVENTS } from '@/constants/EventConstants';
import Locales from '@/locales';
import WCScanConfirmModal from '@/views/ScanProduct/modals/WCScanConfirmModal.vue';
import WCNestedSidebarPanel from '@/modules/sidebar/components/WCNestedSidebarPanel/WCNestedSidebarPanel.vue';
import WCMyAccount from '@/modules/sidebar/components/WCMyAccount/WCMyAccount.vue';
import SideMenuService from '@/modules/sidebar/services/SideMenuService';
import WCSideMenuItemV2 from '../WCSideMenuItemV2/WCSideMenuItemV2.vue';

export default {
  components: {
    WCSidebarV2,
    WCSidebarMenuItemV2,
    WCNestedSidebarPanel,
    WCSideMenuItemV2,
  },
  setup() {
    const { isFederatedLoginEnabled } = useFederatedLogin();
    return { isFederatedLoginEnabled };
  },
  mixins: [
    EmployeeMixin,
    OrderTypesMixin,
    ScanAndGoMixin,
    ItemGroupMixin,
    GiftCardMixin,
    PlatformMixin,
    ContactUsMixin,
  ],
  /**
   * Created hook to fetch circular data for specials and promotions
   */
  async created() {
    this.openListener = () => {
      this.resetNestedSidebar();
      this.scrollSidebarToTop();
    };
    this.toggleListener = () => {
      this.resetNestedSidebar();
      this.scrollSidebarToTop();
    };
    this.closeListener = () => {
      this.resetNestedSidebar();
      this.scrollSidebarToTop();
    };
    this.openMyAccountListener = () => {
      this.$nextTick(() => {
        this.openMyAccountMenu();
        this.scrollSidebarToTop();
      });
    };

    emitter.$on(SIDE_MENU_EVENTS.OPEN, this.openListener);
    emitter.$on(SIDE_MENU_EVENTS.CLOSE, this.closeListener);
    emitter.$on(SIDE_MENU_EVENTS.TOGGLE, this.toggleListener);
    emitter.$on(SIDE_MENU_EVENTS.OPEN_MY_ACCOUNT, this.openMyAccountListener);

    await this.getCircularData();
  },
  beforeUnmount() {
    emitter.$off(SIDE_MENU_EVENTS.OPEN, this.openListener);
    emitter.$off(SIDE_MENU_EVENTS.CLOSE, this.closeListener);
    emitter.$off(SIDE_MENU_EVENTS.TOGGLE, this.toggleListener);
    emitter.$off(SIDE_MENU_EVENTS.OPEN_MY_ACCOUNT, this.openMyAccountListener);
  },
  data() {
    return {
      circularMenu: [],
      isNestedSidebarVisible: false,
      openListener: null,
      closeListener: null,
      toggleListener: null,
      openMyAccountListener: null,
    };
  },
  computed: {
    ...mapGetters({
      isCustomer: 'user/isCustomer',
      isEmployee: 'user/isEmployee',
      isScanAndGoMode: 'user/isCustomerModeScan',
    }),
    cusID() {
      return this.user?.num;
    },
    menu() {
      return this.$configuration.orderTypesEnabled
        ? this.orderTypesMenu
        : this.$configuration.mainMenu;
    },
    currentYear() {
      return new Date().getFullYear();
    },
    contactUsEnabled() {
      return !this.isScanAndGoMode && this.showContactUsPage;
    },
    customerGroupsEnabled() {
      return (
        this.$configuration.customerGroups?.length > 0 &&
        this.$configuration.customerGroups.filter(el => el.groups?.length > 0).length > 0
      );
    },
    buyItAgainEnabled() {
      return this.$configuration.pastItemsEnabled && !this.isScanAndGoMode;
    },
    egiftCardsEnabled() {
      return this.showGiftCardLinks && !this.isScanAndGoMode;
    },
    pushNotificationsEnabled() {
      try {
        const pushNotificationSupportParams = new PushNotificationSupportParams({
          enabledPlatforms: this.$configuration.enabledPlatforms,
          webPushConfig: this.$configuration.webPushConfig,
          publicVapidKey: this.$configuration.publicVapidKey,
          isCustomer: this.isCustomer,
          isEmployee: this.isEmployee,
        });

        return pushNotificationService.isPushNotificationsSupported(pushNotificationSupportParams);
      } catch (error) {
        console.debug('Push Notifications are not supported:', error);
        return false;
      }
    },
    circularLinkText() {
      return this.$configuration.circularLinkText
        ? this.$configuration.circularLinkText
        : this.$t('weeklyAd');
    },
    quickLinks() {
      return this.buildQuickLinks();
    },
    showNestedUtilityMenu() {
      const hasCustomSources = (this.$configuration.customSources || []).length > 0;
      const isEbtEnabled =
        this.isCustomer &&
        this.$configuration.thirdPartyServiceProfiles?.filter(
          profile =>
            profile.thirdPartyServiceProvider?.type === THIRD_PARTY_SERVICE_TYPE.EBT_AUTHORIZATION,
        )?.length;

      return this.isNotAnEmployee && (hasCustomSources || isEbtEnabled);
    },
    currentLanguageName() {
      const configuredLocales = this.$configuration.i18nLanguage;
      const locales = configuredLocales?.length
        ? Locales.filter(locale => configuredLocales.includes(locale.code))
        : Locales;

      const activeLocale = locales.find(locale => locale.code === this.$i18n.locale);
      return activeLocale?.name || this.$i18n.locale;
    },
  },
  watch: {
    $route() {
      this.resetNestedSidebar();
      this.scrollSidebarToTop();
    },
  },
  methods: {
    capitalizeFirstLetter,
    buildQuickLinks() {
      const quickLinks = {
        accountSettings: {
          menuName: this.$t('accountSettings'),
          menuIcon: 'user',
          menuRoute: '/me/edit',
          unauthorizedUsers: [USER_ROLES.EMPLOYEE],
        },
      };

      const quickLinkDefinitions = [
        {
          key: 'contactUs',
          isEnabled: this.contactUsEnabled,
          build: () => ({
            menuName: this.$t('contactUs'),
            menuIcon: 'contact-us',
            menuRoute: '/contact',
            unauthorizedUsers: [USER_ROLES.EMPLOYEE],
          }),
        },
        {
          key: 'accessibilityStatement',
          isEnabled: this.$configuration.accessibilityDisclaimer,
          build: () => ({
            menuName: this.$t('accessibilityStatement'),
            menuIcon: 'accessibility-statement',
            menuRoute: '/accessibility',
          }),
        },
        {
          key: 'customerPrograms',
          isEnabled: this.customerGroupsEnabled,
          build: () => ({
            menuName: this.$t('customerPrograms'),
            menuIcon: 'badge-check',
            menuRoute: '/me/programs',
            unauthorizedUsers: [USER_ROLES.EMPLOYEE],
          }),
        },
        {
          key: 'scanCustomerCard',
          isEnabled: this.$configuration.customerCardEnabled,
          build: () => ({
            menuName: this.$t('scanCustomerCard'),
            menuIcon: 'id-card',
            menuRoute: '/me/card',
            unauthorizedUsers: [USER_ROLES.EMPLOYEE],
          }),
        },
        {
          key: 'acceleratedCheckout',
          isEnabled: !this.isScanAndGoMode && this.$configuration.scanAndGoEnabled,
          build: () => ({
            menuName: this.$t('acceleratedCheckout'),
            menuIcon: 'accelerated-checkout',
            menuRoute: '/scan',
            actionType: 'open-scan-confirm-modal',
            unauthorizedUsers: [USER_ROLES.EMPLOYEE],
          }),
        },
        {
          key: 'rewards',
          isEnabled: this.$configuration.rewardsEnabled,
          build: () => ({
            menuName: this.$t('rewards'),
            menuIcon: 'star',
            menuRoute: '/me/rewards',
            unauthorizedUsers: [USER_ROLES.EMPLOYEE],
          }),
        },
        {
          key: 'shoppingLists',
          isEnabled: this.$configuration.listEnabled,
          build: () => ({
            menuName: this.$t('shoppingLists'),
            menuIcon: 'shopping-lists',
            menuRoute: '/me/lists',
            unauthorizedUsers: [USER_ROLES.EMPLOYEE],
          }),
        },
        {
          key: 'wallet',
          isEnabled: this.$configuration.walletEnabled,
          build: () => ({
            menuName: this.$t('wallet'),
            menuIcon: 'wallet',
            menuRoute: '/me/wallet',
            unauthorizedUsers: [USER_ROLES.EMPLOYEE],
          }),
        },
        {
          key: 'notifications',
          isEnabled: this.pushNotificationsEnabled,
          build: () => ({
            menuName: this.$t('notifications'),
            menuIcon: 'bell',
            menuRoute: '/me/notifications',
            unauthorizedUsers: [USER_ROLES.EMPLOYEE],
          }),
        },
        {
          key: 'weeklyAd',
          isEnabled: this.$configuration.includeCircularLinkInMenu,
          build: () => ({
            menuName: this.circularLinkText,
            menuIcon: 'weekly-ad',
            menuRoute: '/circular',
          }),
        },
        {
          key: 'buyItAgain',
          isEnabled: this.buyItAgainEnabled,
          build: () => ({
            menuName: `${this.$t('reorder')} (${this.$t('buyItAgain')})`,
            menuIcon: 'buy-it-again',
            menuRoute: '/me/items',
            unauthorizedUsers: [USER_ROLES.EMPLOYEE],
          }),
        },
        {
          key: 'orderHistory',
          isEnabled: this.$configuration.pastOrdersEnabled,
          build: () => ({
            menuName: this.$t('orderHistory'),
            menuIcon: 'clock-rotate-left',
            menuRoute: '/me/orders',
            unauthorizedUsers: [USER_ROLES.EMPLOYEE],
          }),
        },
        {
          key: 'digitalCoupons',
          isEnabled: this.$configuration.allowCouponClipping,
          build: () => ({
            menuName: this.$t('digitalCoupons'),
            menuIcon: 'digital-coupons',
            menuRoute: '/cpb',
            unauthorizedUsers: [USER_ROLES.EMPLOYEE],
          }),
        },
        {
          key: 'egiftCards',
          isEnabled: this.egiftCardsEnabled,
          build: () => ({
            menuName: this.$t('giftCards'),
            menuIcon: 'e-gift-cards',
            menuRoute: this.giftCardUrl,
            unauthorizedUsers: [USER_ROLES.EMPLOYEE],
          }),
        },
        {
          key: 'membershipDetails',
          isEnabled: this.$configuration.cmsEnabled,
          build: () => ({
            menuName: this.$t('membershipDetails'),
            menuIcon: 'users',
            menuRoute: '/me/coop',
            unauthorizedUsers: [USER_ROLES.EMPLOYEE],
          }),
        },
        {
          key: 'connectedAccounts',
          isEnabled: this.isFederatedLoginEnabled(this.$configuration),
          build: () => ({
            menuName: this.$t('connectedAccounts'),
            menuIcon: 'connected-accounts',
            menuRoute: '/me/connectedAccounts',
            unauthorizedUsers: [USER_ROLES.EMPLOYEE],
          }),
        },
      ];

      quickLinkDefinitions.forEach(({ key, isEnabled, build }) => {
        if (isEnabled) {
          quickLinks[key] = build();
        }
      });

      return this.filterQuickLinks(quickLinks);
    },
    async logoutAndClose() {
      await this.logout();
      this.closeAndResetSidebar();
    },
    async login() {
      await this.$router.push({ name: 'Login' });
      this.closeAndResetSidebar();
    },
    async register() {
      await this.$router.push({ name: 'Register' });
      this.closeAndResetSidebar();
    },
    async onQuickLinkAction(menu) {
      if (menu?.actionType === 'open-scan-confirm-modal') {
        await this.openScanConfirmModal();
      }
    },
    async openScanConfirmModal() {
      try {
        await ModalService.open(WCScanConfirmModal);
      } catch (error) {
        console.log(error);
      }
    },
    async getCircularData() {
      try {
        const response = await axios.get('/api/circulars');
        this.circularMenu = response?.data?.data;
      } catch (error) {
        console.error(error);
      }
    },
    filterQuickLinks(QUICK_LINKS) {
      return Object.fromEntries(
        Object.entries(QUICK_LINKS).filter(([, link]) => {
          let showAccountsMenu = true;
          if (link.unauthorizedUsers) {
            if (this.isEmployee && this.isCustomer) {
              showAccountsMenu = !link.unauthorizedUsers.includes(USER_ROLES.EMPLOYEE_AS_CUSTOMER);
            } else if (this.isEmployee) {
              showAccountsMenu = !link.unauthorizedUsers.includes(USER_ROLES.EMPLOYEE);
            } else if (this.isCustomer) {
              showAccountsMenu = !link.unauthorizedUsers.includes(USER_ROLES.CUSTOMER);
            } else {
              showAccountsMenu = !link.unauthorizedUsers.includes(USER_ROLES.GUEST);
            }
          }
          return showAccountsMenu;
        }),
      );
    },
    onNestedSidebarVisibilityChange(isVisible) {
      this.isNestedSidebarVisible = isVisible;
    },
    openNestedMenu(title, items, options = {}) {
      this.$refs.nestedSidebarPanel?.openMenu(title, items, options);
    },
    resetNestedSidebar() {
      this.$refs.nestedSidebarPanel?.resetMenu();
      this.isNestedSidebarVisible = false;
    },
    closeAndResetSidebar() {
      this.resetNestedSidebar();
      SideMenuService.close();
    },
    scrollSidebarToTop() {
      this.$nextTick(() => {
        const sidebarPanel = this.$el?.closest('.sidebar-panel');
        if (sidebarPanel) {
          sidebarPanel.scrollTop = 0;
        }
      });
    },
    async performNestedAction(item) {
      if (item.actionType === 'route' && item.route) {
        await this.$router.push(item.route);
        this.closeAndResetSidebar();
        return;
      }

      if (item.actionType === 'locale' && item.localeCode) {
        this.$i18n.locale = item.localeCode;
        localStorage.setItem('locale', item.localeCode);
        this.closeAndResetSidebar();
        return;
      }

      if (item.actionType === 'shop-select' && item.groupPath?.length) {
        await this.handleShopSelectNestedAction(item);
      }
    },
    async handleShopSelectNestedAction(item) {
      if (this.$configuration.orderTypesEnabled && item.orderTypeId) {
        await this.setActiveOrderType(this.orderTypeObjectFromId(item.orderTypeId));
        if (item.groupPath.length === 1) {
          this.navigateToOrderTypeHome(item.orderTypeId);
        } else {
          this.select(item.groupPath);
        }
        this.closeAndResetSidebar();
        return;
      }

      if (item.groupPath.length === 1) {
        this.select(item.groupPath[0]);
      } else {
        this.select(item.groupPath);
      }

      this.closeAndResetSidebar();
    },
    openShopByCategoryMenu() {
      this.openNestedMenu(this.$t('shopByCategory'), this.buildShopByCategoryItems());
    },
    openQuickLinksMenu() {
      this.openNestedMenu(this.$t('quickLinks'), this.buildQuickLinksItems());
    },
    openMyAccountMenu() {
      this.openNestedMenu(this.$t('myAccount'), [], { component: WCMyAccount });
    },
    openLanguageMenu() {
      this.openNestedMenu(this.$t('chooseLanguage'), this.buildLanguageItems());
    },
    buildShopByCategoryItems() {
      const items = [];

      if (this.circularMenu && this.circularMenu.length) {
        items.push({
          id: 'current-specials',
          label: this.$t('currentSpecials'),
          children: this.circularMenu.map(child => ({
            id: `circular-${child.id}`,
            label: child.name,
            actionType: 'shop-select',
            groupPath: [child.id],
          })),
        });
      }

      if (this.menu?.children?.length) {
        this.menu.children.forEach(group => {
          items.push(this.mapShopNode(group, [group.id], group.id));
        });
      }

      return items;
    },
    mapShopNode(node, groupPath, orderTypeId) {
      const hasChildren = node.children && node.children.length;

      if (hasChildren) {
        return {
          id: `shop-${groupPath.join('-')}`,
          label: node.name,
          actionType: 'shop-select',
          groupPath,
          orderTypeId,
          children: node.children.map(child =>
            this.mapShopNode(child, [...groupPath, child.id], orderTypeId),
          ),
        };
      }

      return {
        id: `shop-${groupPath.join('-')}`,
        label: node.name,
        actionType: 'shop-select',
        groupPath,
        orderTypeId,
      };
    },
    buildQuickLinksItems() {
      const customSources = this.$configuration.customSources || [];
      const items = customSources.map(cs => ({
        id: `quick-custom-source-${cs.id}`,
        label: cs.label,
        actionType: 'route',
        route: `/cs/${cs.id}`,
      }));

      const isEbtEnabled =
        this.isCustomer &&
        this.$configuration.thirdPartyServiceProfiles?.filter(
          profile =>
            profile.thirdPartyServiceProvider?.type === THIRD_PARTY_SERVICE_TYPE.EBT_AUTHORIZATION,
        )?.length;

      if (isEbtEnabled) {
        items.push({
          id: 'quick-custom-ebt',
          label: this.$t('checkEbtBalance'),
          actionType: 'route',
          route: '/me/ebt',
        });
      }

      return items;
    },
    buildLanguageItems() {
      const configuredLocales = this.$configuration.i18nLanguage;
      const locales = configuredLocales?.length
        ? Locales.filter(locale => configuredLocales.includes(locale.code))
        : Locales;

      return locales.map(locale => ({
        id: `lang-${locale.code}`,
        label: locale.name,
        actionType: 'locale',
        localeCode: locale.code,
      }));
    },
    async onClickExitScanAndGoMode() {
      this.closeAndResetSidebar();
      const query = { ...this.$route.query };
      if (query.scan === 'true') {
        query.scan = 'false';
        this.$router.replace({ query });
      }
      try {
        if (this.isScanAndGoMode) {
          await this.exitAcceleratedCheckoutMode();
          this.$router.push({ name: 'Home' });
        }
      } catch (e) {
        console.error(e);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/tools';
@import '~@/assets/styles/settings';
@import '~@/assets/styles/_settings.scss';

.side-menu-list {
  list-style: none;
  margin: 0;
  padding: 0;

  li {
    &:first-child {
      border-top: 0;
    }
  }
}

.unstyled-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.user-name--padding {
  padding: $pad-3 * 0.75 $pad-3 * 1.125;
}

.menu__text--color {
  color: var(--dark);
}

.wc-sidebar {
  padding-bottom: $mobile-body-padding-bottom;
  background: var(--light);
  padding-left: 0.75rem;
  padding-right: 0.75rem;
}

.sidebar-v2 {
  --sidebar-v2-card-gap: 0.75rem;
  --sidebar-v2-card-padding-y: 1rem;
  --sidebar-v2-card-padding-x: 1.25rem 0rem;
  --sidebar-v2-row-min-height: 3.125rem;
  --sidebar-v2-icon-size: 1rem;
  position: relative;
  height: 100%;
  min-height: 0;
}

.sidebar-menu {
  border-radius: 0.5rem;
  border: 0.5px solid var(--dark-lighter-5);
  background: var(--white, $white);
  margin: var(--sidebar-v2-card-gap) 0 0;
  padding: var(--sidebar-v2-card-padding-y) var(--sidebar-v2-card-padding-x);
}

.title {
  margin-bottom: 1rem;
  font-size: 1rem;
  font-weight: 700;
}

.menu-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 0;
  border-top: 1px solid var(--dark-lighter-5);
  cursor: pointer;
  transition: background-color 0.15s ease, transform 0.22s ease;
  position: relative;
  isolation: isolate;
}

.menu-item.border-none::before {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  left: -1.25rem;
  right: -1.25rem;
  z-index: -1;
  background-color: transparent;
  transition: background-color 0.15s ease;
}

.border-none {
  border: none;
}

.side-menu-nav {
  color: var(--dark);
  border-top: 0.6rem solid var(--gray-200, $gray-200);
}

.icon-menu--margin {
  margin-left: 0.75rem;
}

.side-menu-icon {
  color: var(--primary, $primary);
  width: var(--sidebar-v2-icon-size);
}

.sidebar-v2__row-link {
  min-height: var(--sidebar-v2-row-min-height);
  display: flex;
  align-items: center;
  justify-content: space-between;
  text-decoration: none;
}

.sidebar-v2__row-button {
  width: 100%;
  min-height: var(--sidebar-v2-row-min-height);
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 0;
  background: transparent;
  padding: 0;
  color: var(--dark, $dark);
  text-align: left;
  transition: transform 0.22s ease;
}

.sidebar-v2__row-button--auto {
  width: auto;
}

.sidebar-v2__row-main {
  display: inline-flex;
  align-items: center;
  min-width: 0;
  gap: 0.75rem;
}

.sidebar-v2__collapsible-label {
  color: var(--dark, $dark);
  font-size: 1rem;
  line-height: 1.2;
}

.sidebar-v2__row-chevron {
  color: var(--dark-lighter-5);
  width: 0.9rem;
}

.sidebar-v2--nested-open > .pt-3 > *:not(.sidebar-v2__nested-panel-host) {
  display: none;
}

.sidebar-v2__signout-wrap {
  margin-top: 0.875rem;
}

.sidebar-v2__signout-button {
  background: var(--primary, $primary);
  border-radius: 999rem;
  min-height: 3rem;
  font-size: 1.375rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.sidebar-v2__signout-button:hover,
.sidebar-v2__signout-button:focus {
  background: var(--primary, $primary);
}

.sidebar-v2__signout-icon {
  color: var(--white);
  margin-right: 0.625rem;
}

.sidebar-v2__signout-label {
  color: var(--white);
}

.sidebar-v2__footer-wrap {
  margin-top: 0.5rem;
}

.sidebar-v2__copyright-wrap {
  color: var(--dark, $dark);
  text-align: center;
  margin-top: 0.75rem;
}

.sidebar-v2-item__left {
  display: inline-flex;
  align-items: center;
  min-width: 0;
  gap: 0.75rem;
}

.sidebar-v2-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  color: var(--dark, $dark);
  text-decoration: none;
}

.sidebar-menu--single-option .menu-item {
  border-radius: 0;
}

@media (hover: hover) and (pointer: fine) {
  .menu-item.border-none:hover::before,
  .menu-item.border-none:focus-within::before {
    background-color: var(--primary-lighter-9, var(--primary));
  }

  .sidebar-menu > .menu-item.border-none:first-child:hover::before,
  .sidebar-menu > .menu-item.border-none:first-child:focus-within::before {
    border-top-left-radius: 0.5rem;
    border-top-right-radius: 0.5rem;
  }

  .sidebar-menu > .menu-item.border-none:last-child:hover::before,
  .sidebar-menu > .menu-item.border-none:last-child:focus-within::before {
    border-bottom-left-radius: 0.5rem;
    border-bottom-right-radius: 0.5rem;
  }

  .menu-item:not(.border-none):hover,
  .menu-item:not(.border-none):focus-within {
    background-color: var(--primary-lighter-9, var(--primary));
  }

  .sidebar-menu > .menu-item:last-child:not(.border-none):hover,
  .sidebar-menu > .menu-item:last-child:not(.border-none):focus-within {
    border-bottom-left-radius: 0.5rem;
    border-bottom-right-radius: 0.5rem;
  }
}

@media (hover: none), (pointer: coarse) {
  .menu-item.border-none:active::before {
    background-color: var(--primary-lighter-9, var(--primary));
  }

  .sidebar-menu > .menu-item.border-none:first-child:active::before {
    border-top-left-radius: 0.5rem;
    border-top-right-radius: 0.5rem;
  }

  .sidebar-menu > .menu-item.border-none:last-child:active::before {
    border-bottom-left-radius: 0.5rem;
    border-bottom-right-radius: 0.5rem;
  }

  .menu-item:not(.border-none):active {
    background-color: var(--primary-lighter-9, var(--primary));
  }

  .sidebar-menu > .menu-item:last-child:not(.border-none):active {
    border-bottom-left-radius: 0.5rem;
    border-bottom-right-radius: 0.5rem;
  }

  .menu-item:active .sidebar-v2-item,
  .menu-item:active .sidebar-v2__row-button {
    transform: scale(0.995);
  }
}

.social-link-section {
  padding: 12px 20px !important;

  .title {
    margin: 0 0 12px;
  }

  .social-icons {
    display: flex;
    align-items: center;
    gap: convert-px-to-rem(17.63);
    margin: 0;
    padding: 0;
  }

  .social-icons li {
    display: contents;
  }

  .social-icons :deep(.wc-icon) {
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
}
</style>
